import { Skill, SkillFormData, SkillProgressTable } from '../types/Skill';
import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface CreateSkillData {
  user_id: string;
  name: string;
  description?: string;
  current_level?: number;
  properties?: { color?: string } | Record<string, any>;
}

export interface UpdateSkillData {
  id: string;
  name?: string;
  description?: string;
  current_level?: number;
  properties?: { color?: string } | Record<string, any>;
}

class SkillService {
  // Create a new skill
  async createSkill(userId: string, skillData: SkillFormData): Promise<{ success: boolean; skill?: Skill; message?: string }> {
    try {
      const data: CreateSkillData = {
        user_id: userId,
        name: skillData.name,
        description: skillData.description || undefined,
        current_level: skillData.current_level || 0,
        properties: skillData.properties || undefined
      };

      const response = await makeAuthenticatedRequest('/skills', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating skill:', error);
      throw error;
    }
  }

  // Get skill by ID
  async getSkillById(skillId: string): Promise<{ success: boolean; skill?: Skill }> {
    try {
      const response = await makeAuthenticatedRequest(`/skills/${skillId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching skill:', error);
      throw error;
    }
  }

  // Get all skills for a user
  async getUserSkills(userId: string): Promise<{ success: boolean; skills?: Skill[]; count?: number }> {
    try {
      const response = await makeAuthenticatedRequest(`/skills/user/${userId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user skills:', error);
      throw error;
    }
  }

  // Update a skill
  async updateSkill(updateData: UpdateSkillData): Promise<{ success: boolean; message?: string; skill?: Skill }> {
    try {
      const { id, ...dataToUpdate } = updateData;
      
      const response = await makeAuthenticatedRequest(`/skills/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToUpdate)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating skill:', error);
      throw error;
    }
  }

  // Delete a skill
  async deleteSkill(skillId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/skills/${skillId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting skill:', error);
      throw error;
    }
  }

  // Link a goal to a skill
  async linkGoalToSkill(skillId: string, goalId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/skills/${skillId}/goals`, {
        method: 'POST',
        body: JSON.stringify({ goal_id: goalId })
      });
      
      return response;
    } catch (error) {
      console.error('Error linking goal to skill:', error);
      throw error;
    }
  }

  // Unlink a goal from a skill
  async unlinkGoalFromSkill(skillId: string, goalId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/skills/${skillId}/goals/${goalId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error unlinking goal from skill:', error);
      throw error;
    }
  }

  // Get yearly progress table for a skill
  async getSkillProgressTable(skillId: string): Promise<SkillProgressTable> {
    try {
      const response = await makeAuthenticatedRequest(`/skills/${skillId}/progress-table`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching skill progress table:', error);
      throw error;
    }
  }
}

export const skillService = new SkillService();

